import { navigation } from '@/app'
import { analytics } from '@/services'
import { Module, Strategy } from '@/types'
import { navigate } from 'gatsby'
import { useCallback, useMemo } from 'react'
import { tryOpenLesson } from '../utils'
import { navigateToHurdle, openURL, resolveStrategyAction } from '@/utils'

type StrategyParams = Module & { onboardingLesson: boolean }

export function useOpenModule() {
  const openModule = useCallback((module: Module) => {
    analytics.track('collection_view', { collection_name: module.title })
    if (module?.chapters?.length === 1) {
      const chapter = module?.chapters?.[0]
      analytics.track('lesson_view', { lesson_name: chapter?.title })
      tryOpenLesson(chapter?.id)
      return
    }

    navigate(`/learn/chapter/${module?.id}` as never)
  }, [])

  const handleNavigateToHurdle = useCallback(() => {
    navigateToHurdle()
    analytics.track('hurdle_start')
  }, [])

  const strategies = useMemo(() => {
    return [
      {
        matches: item => item.type === 'lesson_group',
        execute: item => navigation.navigate('Learn.Content', { id: item.id }),
        name: 'lesson_group',
      },
      {
        matches: item => item.title.toLowerCase() === 'hurdle',
        execute: handleNavigateToHurdle,
        name: 'hurdle',
      },
      {
        matches: item => !!(item.beta_game && item.url),
        execute: item => openURL(item.url),
        name: 'external_link_game',
      },
      {
        matches: item => item.is_free,
        execute: openModule,
        name: 'free',
      },
      {
        matches: item => item.onboardingLesson,
        execute: openModule,
        name: 'onboarding_lesson',
      },
      {
        matches: item => item.is_placeholder,
        execute: () => {},
        name: 'placeholder',
      },
      {
        matches: () => true,
        execute: openModule,
        name: 'default',
      },
    ] satisfies Strategy<StrategyParams>[]
  }, [])

  const resolveModuleAction = useCallback(
    (item: StrategyParams) => {
      return resolveStrategyAction(item, strategies, 'useOpenModule')
    },
    [strategies],
  )

  return { resolveModuleAction, openModule }
}
